'use client';

import Link from '@eliadmin/core/src/components/link';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Button } from '../ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { BlogCard } from '@eliadmin/core/src/components/blog/blog-card';

import { HiOutlineArrowRightCircle } from 'react-icons/hi2';
import { CarouselProgress } from '../ui/carousel-progress';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';

export function Blogs({
  blogs,
  className,
}: {
  blogs: Blog[];
  className?: { section?: string; container?: string };
}) {
  const { t } = useTranslations();

  const options = {
    align: 'start' as const,
  };

  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex } = carouselContext;

  return (
    <section
      className={cn(
        'custom-section',
        'gap-8 pb-8 pt-16 sm:gap-10 sm:pb-12 sm:pt-24 lg:gap-12 lg:pb-[72px] lg:pt-[144px]',
        className?.section,
      )}
    >
      <div
        className={cn('custom-container', 'flex items-end justify-between', className?.container)}
      >
        <Heading subtitle="BLOG" title={t('text3221')} />
        <Button asChild variant={'outline'} className="hidden sm:flex">
          <Link prefetch={false} href={ROUTES.BLOG()}>
            {t('text2000')} <HiOutlineArrowRightCircle size={24} />
          </Link>
        </Button>
      </div>
      <div className={cn('custom-container', 'relative')}>
        <div className="absolute -left-[1000px] top-0 z-[30] h-full w-[1000px] cursor-context-menu bg-white" />
        <Carousel
          options={options}
          className={{
            container: 'w-full gap-4 overflow-visible sm:gap-6 lg:gap-8',
            main: 'w-full overflow-visible',
            wrapper: 'w-full overflow-visible',
            slide: 'w-full max-w-[300px] sm:max-w-[340px] md:max-w-[400px] lg:max-w-[448px]',
          }}
          slides={blogs.map(blog => (
            <BlogCard
              blog={blog}
              key={blog.slug}
              className={{
                blogImageContainer: 'h-[193px] sm:h-[220px] md:h-[235px] lg:h-[265px] xl:h-[288px]',
              }}
            />
          ))}
        />
      </div>
      <div className="block w-full pr-4 sm:hidden">
        <CarouselProgress selectedIndex={selectedIndex} totalSlides={blogs.length} />
      </div>
      <Button asChild variant={'outline'} className="flex w-full sm:hidden">
        <Link prefetch={false} href={ROUTES.BLOG()}>
          {t('text2000')} <HiOutlineArrowRightCircle size={24} />
        </Link>
      </Button>
    </section>
  );
}
